<template>
    <div class='priceplan-details'>
      <dean-two-column-layout :title="headerTitle">
        <dean-section-title-item
            :icon="saveIcon"
            slot="title-items"
            :content="true"
            :large="true"
            @click.native="save"
        >
          Opslaan
        </dean-section-title-item>
        <div class="column" slot="left">
          <dean-select-form-row id="product" label="Product" :options="products" placeholder="kies een product"
                                :value="currentlySelectedOption(data.product)" @input="valueChanged"
                                :required-field="true" :warning="$v.data.product.$error"
                                @focus="$v.data.product.$touch()"/>
          <dean-form-row id="connectPrice" label="Aansluitkosten" v-model="data.connectPrice" type="number"
                         :required-field="true" :warning="$v.data.connectPrice.$error"
                         @focus="$v.data.connectPrice.$touch()"/>
          <dean-form-row id="recurringPrice" label="Maandelijkse kosten" v-model="data.recurringPrice" type="number"
                         :required-field="true" :warning="$v.data.recurringPrice.$error"
                         @focus="$v.data.recurringPrice.$touch()"/>
          <dean-form-row id="disconnectPrice" label="Afsluitkosten" v-model="data.disconnectPrice" type="number"
                         :required-field="true" :warning="$v.data.disconnectPrice.$error"
                         @focus="$v.data.disconnectPrice.$touch()"/>
          <dean-form-row id="startDate" label="Startdatum" v-model="data.startDate" type="date"
                         :required-field="true" :warning="$v.data.startDate.$error"
                         @focus="$v.data.startDate.$touch()"/>
          <dean-form-row id="endDate" label="Einddatum" v-model="data.endDate" type="date"
                         :required-field="true" :warning="$v.data.endDate.$error"
                         @focus="$v.data.endDate.$touch()"/>
        </div>
      </dean-two-column-layout>
    </div>
</template>

<script lang="ts">
  import {Vue, Component} from 'vue-property-decorator';
  import {
    deanSectionTitleItem,
    saveIcon,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import DeanNullableSwitchFormRow from '@/components/deanComponents/deanNullableSwitchFormRow.vue';
  import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
  import {validationMixin} from 'vuelidate';
  import {required} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';
  import {createNotification} from '@/mixins/validations';
  import {deanDialogBus} from '@/main';

  import {getDutchDateFormat} from '@/mixins/dateWrapper';

  @Component({
    components: {
      DeanSelectFormRow,
      deanSectionTitleItem,
      DeanFormRow,
      DeanTwoColumnLayout,
      DeanNullableSwitchFormRow,
    },
    mixins: [validationMixin],
  })
  export default class Details extends Vue {
    // DATA
    public data: any = {
      product: null,
      connectPrice: null,
      recurringPrice: null,
      disconnectPrice: null,
      startDate: null,
      endDate: null,
    };
    public products: any = [];
    public requestParams: any = {
      id: null,
      ppId: null,
      startDate: null,
      endDate: null,
    };
    public saveIcon: any = saveIcon;

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          product: {required},
          connectPrice: {required},
          recurringPrice: {required},
          disconnectPrice: {required},
          startDate: {required},
          endDate: {required},
        },
      };
    }

    get headerTitle(): string {
      return (this.$route.params.id !== undefined) ? 'Prijsplan regel wijzigen' : 'Prijsplan regel toevoegen';
    }

    // METHODS
    public currentlySelectedOption(value: any) {
      if (value !== null) {
        const foundKey = Object.keys(this.products).find((key) => this.products[key].value === value);

        return foundKey != null ? this.products[foundKey] : null;
      }

      return null;
    }

    public valueChanged(field: any) {
      this.data.product = field.value;
    }

    public save() {
      this.$v.$touch();

      if (! this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);

        this.$store.dispatch('savePricePlanEntry', {
          id: this.requestParams.id,
          pricePlanId: this.requestParams.ppId,
          startDate: this.data.startDate,
          endDate: this.data.endDate,
          productId: this.data.product,
          connectPrice: this.data.connectPrice,
          recurringPrice: this.data.recurringPrice,
          disconnectPrice: this.data.disconnectPrice,
        }).then(
          (response) => {
            this.$toast(this.$store.state.main.notification.messages.dataSaved);

            this.$router.push({name: 'standingData.pricePlan.entries.filter', params: {
              startDate: this.data.startDate,
              endDate: this.data.endDate,
            }});
          },
          (error) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          },
        ).finally(() => {
          this.$store.dispatch('setLoadOverlay', false);
        });
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public created() {
      this.$store.dispatch('setLoadOverlay', true);

      this.requestParams = {
        ppId: this.$route.params.ppId,
        id: this.$route.params.id,
        startDate: this.$route.params.startDate,
        endDate: this.$route.params.endDate,
      };

      if (this.$route.params.id !== undefined) {
        this.loadInfo();
      } else {
        this.data.startDate = this.requestParams.startDate;
        this.data.endDate = this.requestParams.endDate;

        this.$store.dispatch('requestPricePlanEntryAvailableProducts', {
          pricePlanId: this.$route.params.ppId,
          startDate: this.$route.params.startDate,
          endDate: this.$route.params.endDate,
        })
        .then(
          (response) => {
            this.products = response.data.map((product: any) => {
              return {
                value: product.id,
                label: product.privateName + ' / ' + product.code,
              };
            });
          },
          (error) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
          },
        )
        .finally(() => {
          this.$store.dispatch('setLoadOverlay', false);
        });
      }
    }

    public loadInfo(): void {
      this.$store.dispatch('requestPricePlanEntry', {
        id: this.$route.params.id,
        pricePlanId: this.$route.params.ppId,
      })
      .then(
        (response) => {
          const pricePlanEntry = response.data;

          this.data = {
            id: pricePlanEntry.id,
            product: pricePlanEntry.product ? pricePlanEntry.product.id : '',
            connectPrice: pricePlanEntry.connectPrice ? pricePlanEntry.connectPrice : '0',
            recurringPrice: pricePlanEntry.recurringPrice ? pricePlanEntry.recurringPrice : '0',
            disconnectPrice: pricePlanEntry.disconnectPrice ? pricePlanEntry.disconnectPrice : '0',
            startDate: pricePlanEntry.startDate,
            endDate: pricePlanEntry.endDate,
          };

          return this.$store.dispatch('requestPricePlanEntryAvailableProducts', {
            pricePlanId: this.$route.params.ppId,
            startDate: this.data.startDate,
            endDate: this.data.endDate,
            productId: this.data.product,
          });
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (response) => {
          this.products = response.data.map((product: any) => {
            return {
              value: product.id,
              label: product.privateName + ' / ' + product.code,
            };
          });
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    public mounted() {
      deanDialogBus.$on('deleteStatusChanged', () => {
        this.$router.push({name: 'standingData.pricePlan.entries'});
      });
    }

    public destroyed() {
      deanDialogBus.$off('deleteStatusChanged');
    }
  }
</script>

<style lang="scss" scoped>

</style>
